import { useMainContext } from '@/lib/context';
import { getAutocompleteOptions, getResults } from '@/lib/getResults';
import { getMapSettings } from '@/lib/levelsHelpers';
import {
  AutocompleteOption,
  DataLevel,
  ResultCodes,
  StructureType,
} from '@/lib/model';
import * as React from 'react';
import { MainComponent } from './MainComponent';

const TABS = [
  {
    label: 'Camera',
    value: StructureType.CAMERA,
  },
  {
    label: 'Senato',
    value: StructureType.SENATE,
  },
];

const FILTERS = [
  {
    label: 'Circoscrizioni',
    value: DataLevel.CIRCOSCRIZIONE,
  },
  {
    label: 'Collegi',
    value: DataLevel.COLLEGIO,
  },
  {
    label: 'Comuni',
    value: DataLevel.COMUNE,
  },
];

export const Main = () => {
  const {
    dataLevel,
    setDataLevel,
    structureType,
    setResults,
    setNationalResult,
    handleResult,
    selectedResults,
    lastResult,
    lastCollegio,
    setComuneIsLoading,
    lastVersion,
    is2018,
  } = useMainContext();

  // All results
  React.useEffect(() => {
    const getFetchResults = () => {
      // get comuni by collegio id
      if (dataLevel === DataLevel.COMUNE && lastCollegio) {
        return getResults(
          is2018,
          structureType,
          dataLevel,
          lastCollegio.idCodes
        );
      }

      // get all data
      return getResults(is2018, structureType, dataLevel);
    };

    const fn = async () => {
      const results = await getFetchResults();

      setResults(results);
    };

    fn();
  }, [is2018, structureType, dataLevel, lastCollegio, setResults, lastVersion]);

  // National result
  React.useEffect(() => {
    const fn = async () => {
      let nationalResult = (
        await getResults(is2018, structureType, DataLevel.NAZIONALE)
      )[0];

      if (!nationalResult) {
        return;
      }

      nationalResult = {
        ...nationalResult,
        mapLayer: {
          ...nationalResult.mapLayer,
          coordinates: [14.2430069, 40.8536072], // Italian coordinates
        },
      };

      setNationalResult(nationalResult);
    };

    fn();
  }, [is2018, structureType, setNationalResult, lastVersion]);

  if (!selectedResults.length) {
    return null;
  }

  const nationalMapSettings = getMapSettings(
    structureType,
    DataLevel.NAZIONALE
  );
  let mapSettings = getMapSettings(structureType, dataLevel);
  if (lastResult?.level === DataLevel.NAZIONALE) {
    mapSettings = {
      ...mapSettings,
      zoom: nationalMapSettings.zoom,
    };
  }

  const filters = FILTERS.filter(f => {
    // show comune tab iff there is a selected collegio
    if (!lastCollegio && f.value === DataLevel.COMUNE) {
      return false;
    }

    return true;
  });

  const onOptionPress = async (value: AutocompleteOption) => {
    setComuneIsLoading(true);

    const codes: ResultCodes = {
      codCirc: value.cod_circ,
      codClPlu: value.cod_plu,
      codClUni: value.cod_cl_uni,
      codCom: value.cod_comune,
      codProv: value.cod_provincia,
      codReg: value.cod_regione,
    };

    try {
      const collegi = await getResults(
        is2018,
        structureType,
        DataLevel.COLLEGIO
      );
      const collegio = collegi.find(
        collegio => collegio.idCodes.codClUni === codes.codClUni
      );

      const comune = await getResults(
        is2018,
        structureType,
        DataLevel.COMUNE,
        codes,
        true
      );

      if (!collegio || !comune.length) {
        return;
      }

      handleResult([collegio, comune[0]], false);
      setDataLevel(DataLevel.COMUNE);
    } catch {
      setComuneIsLoading(false);
    }
  };

  const inputOptions = getAutocompleteOptions(structureType);

  return (
    <MainComponent
      inputOptions={inputOptions}
      onOptionPress={onOptionPress}
      mapSettings={mapSettings}
      tabs={TABS}
      filters={filters}
    />
  );
};
